import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { popularRouteAPI } from '../../services/api';

const API_HOST = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
const resolveUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_HOST}${url}`);

const EMPTY_FORM = { fromCity: '', toCity: '', dailyTrips: 1, minHours: 1, maxHours: 2, displayOrder: 0 };

/* ── Inline image uploader shown in each row ── */
const ImageUploader = ({ row, onUploaded }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(resolveUrl(row.imageUrl));

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const res = await popularRouteAPI.uploadImage(row.id, file);
      onUploaded(row.id, res.data.imageUrl);
      toast.success('تم رفع الصورة');
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل رفع الصورة');
      setPreview(resolveUrl(row.imageUrl));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {preview ? (
        <img src={preview} alt="" className="w-14 h-10 object-cover rounded-lg border border-gray-200" />
      ) : (
        <div className="w-14 h-10 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-300 text-xs">
          لا صورة
        </div>
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="text-xs text-blue-600 hover:text-blue-800 font-semibold transition disabled:opacity-50"
      >
        {uploading ? 'جاري الرفع...' : preview ? 'تغيير' : 'رفع صورة'}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
    </div>
  );
};

const PopularRoutesPage = () => {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [adding, setAdding]     = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving]     = useState(false);

  const load = () => {
    setLoading(true);
    popularRouteAPI.getAll(true)
      .then((res) => setRows(res.data.data || []))
      .catch(() => toast.error('فشل تحميل الوجهات'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.fromCity.trim() || !form.toCity.trim()) return;
    setAdding(true);
    try {
      await popularRouteAPI.create(form);
      toast.success('تمت الإضافة — ارفع صورة للوجهة من القائمة أدناه');
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإضافة');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditData({
      fromCity: row.fromCity, toCity: row.toCity,
      dailyTrips: row.dailyTrips, minHours: row.minHours, maxHours: row.maxHours,
      isEnabled: row.isEnabled, displayOrder: row.displayOrder,
    });
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await popularRouteAPI.update(id, editData);
      toast.success('تم الحفظ');
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    try {
      await popularRouteAPI.update(row.id, { isEnabled: !row.isEnabled });
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, isEnabled: !r.isEnabled } : r));
    } catch {
      toast.error('فشل التحديث');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`حذف "${row.fromCity} → ${row.toCity}"؟`)) return;
    try {
      await popularRouteAPI.remove(row.id);
      toast.success('تم الحذف');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحذف');
    }
  };

  const handleImageUploaded = (id, imageUrl) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, imageUrl } : r));
  };

  const f  = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const ef = (key) => (e) => setEditData((p) => ({ ...p, [key]: e.target.value }));

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">

      <div>
        <h1 className="text-2xl font-bold text-gray-800">🗺️ الوجهات الشائعة</h1>
        <p className="text-sm text-gray-500 mt-1">تظهر في الصفحة الرئيسية للمسافرين — أضف وجهة ثم ارفع لها صورة.</p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="bg-white rounded-2xl shadow p-5 space-y-4">
        <h2 className="font-semibold text-gray-700 text-sm">إضافة وجهة جديدة</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">من (مدينة المغادرة)</label>
            <input value={form.fromCity} onChange={f('fromCity')} placeholder="مثال: الخرطوم" required
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">إلى (مدينة الوصول)</label>
            <input value={form.toCity} onChange={f('toCity')} placeholder="مثال: بورتسودان" required
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">عدد الرحلات اليومية</label>
            <input type="number" min="1" value={form.dailyTrips} onChange={f('dailyTrips')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">أقل وقت سفر (ساعة)</label>
            <input type="number" min="1" value={form.minHours} onChange={f('minHours')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">أقصى وقت سفر (ساعة)</label>
            <input type="number" min="1" value={form.maxHours} onChange={f('maxHours')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">ترتيب العرض</label>
            <input type="number" min="0" value={form.displayOrder} onChange={f('displayOrder')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
        </div>
        <button type="submit" disabled={adding || !form.fromCity.trim() || !form.toCity.trim()}
          className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-white font-semibold rounded-xl px-6 py-2 text-sm transition">
          {adding ? '...' : '+ إضافة وجهة'}
        </button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="px-5 py-3 border-b">
          <span className="font-semibold text-gray-700 text-sm">الوجهات ({rows.length})</span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">جاري التحميل...</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">لا توجد وجهات بعد</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-right font-medium">الصورة</th>
                  <th className="px-4 py-3 text-right font-medium">الوجهة</th>
                  <th className="px-4 py-3 text-right font-medium">رحلات يومية</th>
                  <th className="px-4 py-3 text-right font-medium">وقت السفر</th>
                  <th className="px-4 py-3 text-right font-medium">الترتيب</th>
                  <th className="px-4 py-3 text-right font-medium">الحالة</th>
                  <th className="px-4 py-3 text-left  font-medium">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row) => (
                  <tr key={row.id} className={`hover:bg-gray-50/60 ${!row.isEnabled ? 'opacity-50' : ''}`}>
                    {editId === row.id ? (
                      <>
                        <td className="px-4 py-2">
                          <ImageUploader row={row} onUploaded={handleImageUploaded} />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1 items-center">
                            <input value={editData.fromCity} onChange={ef('fromCity')}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-24 focus:outline-none" />
                            <span className="text-gray-400">→</span>
                            <input value={editData.toCity} onChange={ef('toCity')}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-24 focus:outline-none" />
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <input type="number" min="1" value={editData.dailyTrips} onChange={ef('dailyTrips')}
                            className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-16 focus:outline-none" />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-1 items-center">
                            <input type="number" min="1" value={editData.minHours} onChange={ef('minHours')}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-12 focus:outline-none" />
                            <span className="text-xs text-gray-400">-</span>
                            <input type="number" min="1" value={editData.maxHours} onChange={ef('maxHours')}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-12 focus:outline-none" />
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <input type="number" min="0" value={editData.displayOrder} onChange={ef('displayOrder')}
                            className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-14 focus:outline-none" />
                        </td>
                        <td className="px-4 py-2">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editData.isEnabled}
                              onChange={(e) => setEditData((p) => ({ ...p, isEnabled: e.target.checked }))} />
                            <span className="text-xs">{editData.isEnabled ? 'مفعّل' : 'معطّل'}</span>
                          </label>
                        </td>
                        <td className="px-4 py-2 text-left">
                          <button onClick={() => handleSave(row.id)} disabled={saving}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg ml-1 transition">
                            {saving ? '...' : 'حفظ'}
                          </button>
                          <button onClick={() => setEditId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 transition">
                            إلغاء
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <ImageUploader row={row} onUploaded={handleImageUploaded} />
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-800">
                          {row.fromCity} <span className="text-blue-500">→</span> {row.toCity}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{row.dailyTrips} رحلة</td>
                        <td className="px-4 py-3 text-gray-600">{row.minHours} - {row.maxHours} ساعات</td>
                        <td className="px-4 py-3 text-gray-500">{row.displayOrder}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleToggle(row)}
                            className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                              row.isEnabled
                                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                            }`}>
                            {row.isEnabled ? 'مفعّل' : 'معطّل'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <button onClick={() => startEdit(row)}
                            className="text-xs text-blue-600 hover:text-blue-800 ml-3 transition">
                            تعديل
                          </button>
                          <button onClick={() => handleDelete(row)}
                            className="text-xs text-red-500 hover:text-red-700 transition">
                            حذف
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PopularRoutesPage;
